// src/views/words-lists/components/TreeNode.tsx
import React, {
  FC, useCallback, useRef, useEffect,
} from 'react';
import { NodeApi, NodeRendererProps } from 'react-arborist';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FolderOutlined,
  FolderFilled,
  FolderOpenFilled,
  FileTextOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { TreeNodeType } from '../../../services/ApiService';
import styles from '../FolderTreeView.module.less';

interface TreeNodeProps extends NodeRendererProps<TreeNodeType> {
  isActive: boolean;
  isBulkMode: boolean;
  isBulkSelected: boolean;
  searchQuery: string;
  onItemClick: (node: NodeApi<TreeNodeType>) => void;
  onToggleActive: (node: NodeApi<TreeNodeType>) => void;
  onToggleBulkSelection: (nodeId: string) => void;
  onContextMenu?: (node: NodeApi<TreeNodeType>, e: React.MouseEvent) => void;
}

export const TreeNode: FC<TreeNodeProps> = ({
  node,
  style,
  dragHandle, // Zachowujemy dla kompatybilności z react-arborist
  isActive,
  isBulkMode,
  isBulkSelected,
  searchQuery,
  onItemClick,
  onToggleActive,
  onToggleBulkSelection,
  onContextMenu,
}) => {
  const { t } = useTranslation();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isFolder = node.data.type === 'folder';
  const isSearchMatch = searchQuery
    && node.data.name.toLowerCase().includes(searchQuery.toLowerCase());

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isBulkMode) {
      onToggleBulkSelection(node.id);
    } else if (!isFolder) {
      onToggleActive(node);
    } else {
      onItemClick(node);
    }
  }, [isBulkMode, isFolder, node, onItemClick, onToggleActive, onToggleBulkSelection]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) {
      onContextMenu(node, e);
    }
  }, [node, onContextMenu]);

  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return '';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (Number.isNaN(dateObj.getTime())) return '';
      const day = dateObj.getDate().toString().padStart(2, '0');
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      const year = dateObj.getFullYear();

      return `${day}.${month}.${year}`;
    } catch (error) {
      console.error('Date formatting error:', error);

      return '';
    }
  };

  const nodeClasses = [
    styles.treeNode,
    isActive && styles.activeNode,
    node.isSelected && styles.selectedNode,
    isBulkSelected && styles.bulkSelected,
  ].filter(Boolean).join(' ');

  const iconClasses = isFolder ? styles.folderIcon : styles.listIcon;
  const nameClasses = [
    styles.nodeName,
    isFolder ? styles.folderName : styles.listName,
    isSearchMatch && styles.highlightedText,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={dragHandle} // Zachowujemy dla kompatybilności
      style={style}
      className={nodeClasses}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleClick(e as any);
        } else if (e.key === ' ') {
          e.preventDefault();
          // Spację obsługuje też samo drzewo (react-arborist) — bez stopPropagation zrobiłoby to samo drugi raz
          if (!isBulkMode && isFolder) {
            e.stopPropagation();
            onItemClick(node);
          } else if (isBulkMode) {
            e.stopPropagation();
            onToggleBulkSelection(node.id);
          }
        }
      }}
    >
      {/* Okrągłe jak pola do zaznaczania w całej aplikacji */}
      {isBulkMode && (
        <CheckCircleOutlined
          className={`${styles.bulkCheckbox} ${isBulkSelected ? styles.bulkCheckboxActive : ''}`}
        />
      )}

      <span className={`${styles.nodeIcon} ${iconClasses}`}>
        {isFolder ? (
          node.isOpen ? <FolderOpenFilled /> : <FolderFilled />
        ) : (
          <FileTextOutlined />
        )}
      </span>

      <span className={nameClasses}>
        {node.data.name}
      </span>

      <span className={styles.nodeInfo}>
        {isFolder && node.data.data && (
          <>
            {node.data.data.subfoldersCount !== undefined && node.data.data.subfoldersCount > 0 && (
              <Tooltip title={t('folders:tree.subfolders', { n: node.data.data.subfoldersCount })}>
                <FolderOutlined style={{
                  marginLeft: 8, fontSize: 12, opacity: 0.6, color: 'white',
                }}
                />
              </Tooltip>
            )}

            {node.data.data.totalListsCount !== undefined && node.data.data.totalListsCount > 0 && (
              <Tooltip title={t('folders:tree.listsCount')}>
                <span className={styles.countBadge}>
                  {node.data.data.totalListsCount}
                </span>
              </Tooltip>
            )}
          </>
        )}

        {!isFolder && (
          <>
            <Tooltip title={t('folders:tree.createdAt')}>
              <span className={styles.createdDate}>
                {node.data.data?.createdAt ? (
                  formatDate(node.data.data.createdAt)
                ) : (
                  <span>&nbsp;</span>
                )}
              </span>
            </Tooltip>

            <Tooltip title={t('folders:tree.wordsCount')}>
              <span className={styles.wordCount}>
                {node.data.data?.wordsCount || 0}
              </span>
            </Tooltip>
          </>
        )}
      </span>
    </div>
  );
};

export default TreeNode;
